<script setup lang="ts">
import { ref } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import { TuBasePopup, ButtonSeverity, TuButton } from "@tinyurldev/tu-ui-vue";

import useNotify from "@Composables/useNotify";

//
// Setup
const { notifyError } = useNotify();

//
// Props
interface Props {
    title: string;
    entityName: string;
    inviterName: string;
    roleName: string | null;
    acceptInvitation: () => Promise<void>;
    rejectInvitation: () => Promise<void>;
    notifyClosed: (succeeded: boolean) => void;
}

const props = defineProps<Props>();

//
// Emit
const emit = defineEmits<{
    (e: "close"): void
}>();

//
// Data
const isLoading = ref(false);

//
// Computed
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Methods
async function handleAccept(): Promise<void> {
    await handleAction(props.acceptInvitation);
}

async function handleReject(): Promise<void> {
    await handleAction(props.rejectInvitation);
}

async function handleAction(action: () => Promise<void>): Promise<void> {
    isLoading.value = true;

    let succeeded = false;

    try {
        await action();
        succeeded = true;
    } catch {
        notifyError({ text: "Something went wrong while processing the invitation. Please try again." });
    } finally {
        isLoading.value = false;
        props.notifyClosed(succeeded);
        emit("close");
    }
}

//
// Exposed Test Vars
defineExpose({ isLoading });
</script>

<template>
  <FocusTrap :escape-deactivates="false">
    <TuBasePopup
      id="invitation-popup"
      tabindex="-1"
      class="invitation-popup _invitation_popup"
    >
      <template #title>
        <span class="_title">{{ props.title }}</span>
      </template>
      <template #content>
        <div class="invitation-popup__content">
          <p class="invitation-popup__message _message">
            <strong class="_inviter">{{ props.inviterName }}</strong> has invited you to join
            <strong class="_entity">{{ props.entityName }}</strong>
            <template v-if="props.roleName">
              as <strong class="_role">{{ props.roleName }}</strong>
            </template>
          </p>
        </div>
      </template>
      <template #footer>
        <div class="invitation-popup__buttons">
          <TuButton
            class="_reject"
            label="Decline"
            is-outlined
            :severity="ButtonSeverity.CANCEL"
            :disabled="isLoading"
            @click="handleReject"
          />
          <TuButton
            class="_accept"
            :is-block="isMobileView"
            label="Accept"
            :disabled="isLoading"
            @click="handleAccept"
          />
        </div>
      </template>
    </TuBasePopup>
  </FocusTrap>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.invitation-popup {
    width: 100%;
    max-width: none;

    &__content {
        font-size: typography.$base-font-size;
        line-height: typography.$base-line-height;
    }

    &__message {
        margin: 0;
    }

    &__buttons {
        display: flex;
        justify-content: flex-end;
        align-items: end;
        gap: spacing.$xxs;
    }
}
</style>
